import { useCallback, useId, useMemo } from 'react'
import type { MouseEvent } from 'react'
import type { DisplayMode, DisplayModeConfig } from '../types'
import type { UseReqdeskTriggerOptions, UseReqdeskTriggerReturn } from './types'
import { useRegistry, useRegistrySnapshot } from './registry-context'

function applyDisplayOverride(
  setDisplayMode: (mode: DisplayMode, side?: DisplayModeConfig['side']) => void,
  display: UseReqdeskTriggerOptions['display'],
): void {
  if (!display) return
  if (typeof display === 'string') {
    setDisplayMode(display)
  } else if (display.mode) {
    setDisplayMode(display.mode, display.side)
  }
}

export function useReqdeskTrigger(opts: UseReqdeskTriggerOptions = {}): UseReqdeskTriggerReturn {
  const registry = useRegistry()
  const isOpen = useRegistrySnapshot(s => s.isOpen)
  const controlsId = useId()

  const targetRef = opts.target
  const displayRef = opts.display

  const onClick = useCallback((e?: MouseEvent<HTMLElement>) => {
    if (e?.currentTarget) registry.recordActivator(e.currentTarget)
    applyDisplayOverride(registry.setDisplayMode, displayRef)
    if (!targetRef || targetRef === 'menu') {
      registry.openMenu()
    } else {
      registry.openAction(targetRef.actionId)
    }
  }, [registry, targetRef, displayRef])

  const ariaProps = useMemo(() => ({
    'aria-haspopup': 'menu' as const,
    'aria-expanded': isOpen,
    'aria-controls': `rqd-panel-${controlsId}`,
  }), [isOpen, controlsId])

  return { onClick, isOpen, ariaProps }
}
